import { useEffect, useState } from 'react';
import { useGameStore } from '@/ui/store/gameStore';
import type { EffectStat } from '@/config/potions.config';
import { Icon } from './Icon';

/**
 * ActiveBuffsHud — pasek aktywnych buffów z mikstur.
 *
 * Renderowany w lewym słupku HUD, POD panelem walut (App.tsx) — buff złota ląduje
 * wizualnie pod licznikiem złota. Czyta `store.activeEffects`, tyka co 1 s i pokazuje
 * chipy z odliczaniem czasu (mm:ss) oraz siłą buffa (+X%). Wygasłe znikają
 * (filtr `expiresAt > now`). Klik w pasek otwiera plecak mikstur. Brak aktywnych
 * buffów → nic nie renderuje (słupek po prostu kończy się na walutach).
 */

const STAT_META: Record<EffectStat, { icon: string; label: string; color: string }> = {
  gold: { icon: '/ui/ui_potion_gold.webp', label: 'Złoto', color: 'text-amber-700' },
  luck: { icon: '/ui/ui_potion_luck.webp', label: 'Szczęście', color: 'text-emerald-700' },
  strength: { icon: '/ui/ui_potion_strength.webp', label: 'Siła', color: 'text-rose-700' },
};

const STAT_ORDER: readonly EffectStat[] = ['gold', 'luck', 'strength'];

export function ActiveBuffsHud(): JSX.Element | null {
  const activeEffects = useGameStore((s) => s.activeEffects);
  const setOpenModal = useGameStore((s) => s.setOpenModal);
  const [now, setNow] = useState(() => Date.now());

  // Tick co 1 s — odliczanie + auto-ukrycie wygasłych. Interval żyje cały czas
  // (komponent zawsze zamontowany), ale to 1 setState/s — pomijalne.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const active = STAT_ORDER.map((stat) => ({ stat, e: activeEffects[stat] })).filter(
    (x): x is { stat: EffectStat; e: { mult: number; expiresAt: number } } =>
      x.e !== undefined && x.e.expiresAt > now,
  );

  if (active.length === 0) return null;

  return (
    <button
      type="button"
      onClick={() => setOpenModal('potionBag')}
      className="pointer-events-auto flex flex-col items-start gap-1.5 bg-transparent"
      aria-label="Aktywne mikstury"
    >
      {active.map(({ stat, e }) => {
        const meta = STAT_META[stat];
        const pct = Math.round((e.mult - 1) * 100);
        return (
          <div
            key={stat}
            className="bg-white/90 rounded-2xl pl-1.5 pr-2.5 py-1 shadow-md flex items-center gap-1.5"
            title={`${meta.label} +${pct}%`}
          >
            <Icon src={meta.icon} fallback="🧪" alt={meta.label} className="w-6 h-6" />
            <span className={`text-xs font-bold tabular-nums ${meta.color}`}>+{pct}%</span>
            <span className="text-xs font-bold text-gray-500 tabular-nums">
              {formatRemaining(e.expiresAt - now)}
            </span>
          </div>
        );
      })}
    </button>
  );
}

/** Pozostały czas w formacie mm:ss (clamp do 0). */
function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
